import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plane, Users, TrendingUp, Award, MapPin, Star, PlaneTakeoff,
  Sparkles, Languages, Coffee, Luggage, Building2, Compass,
  X, Mail, Camera, Armchair, Wallet, ShieldCheck, Zap
} from 'lucide-react';
import Footer from '@/components/Footer';
import { AI_LEADERSHIP } from '@/data/aiLeadership';
import PageSeo from '@/components/PageSeo';

/* ── Shared glass panel (matches HowItWorks / marketing surfaces) ── */
function Panel({ children, className = '', innerClassName = 'p-6 sm:p-7 md:p-8' }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.02] backdrop-blur-sm ${className}`}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      <div className={`relative ${innerClassName}`}>{children}</div>
    </div>
  );
}

/* Bump to force browsers to refetch team photos after a file changes */
const TEAM_IMG_VERSION = 2;

/* ── Leadership data ── */
const LEADERS = [
  {
    slug: 'jayjay-gonzalez',
    name: 'JayJay Gonzalez',
    initials: 'JG',
    imgScale: 1.12,
    role: 'Founder & Chief Executive Officer',
    shortRole: 'Founder & CEO',
    quote: 'Travel isn\u2019t just where you go\u2014it\u2019s who you become along the way.',
    bio: [
      'JayJay Gonzalez is a Harvard student, founder, and operator whose work has crossed technology, travel, services, hospitality, and creative industries. He founded Defera after realizing that airfare remained one of the last major travel expenses requiring full payment upfront. He envisioned a platform that made premium travel financially flexible while maintaining exceptional customer care.',
      'Before Defera, he spent years building consumer technology products and advising early-stage startups, where he saw firsthand how thoughtful design and fair pricing could open doors for people traditional companies overlooked. That experience shaped Defera\u2019s founding principle: financial flexibility should never come at the cost of a premium experience.',
      'Today, he leads Defera\u2019s long-term vision, product strategy, strategic partnerships, and company culture. He stays close to the details\u2014reading customer feedback daily, reviewing itineraries, and testing every feature before it ships. When he\u2019s not building, he\u2019s usually behind the wheel of a sports car, driving for hours.'
    ],
    passport: [
      { icon: Languages, label: 'Languages', value: 'English, Spanish' },
      { icon: Coffee, label: 'Coffee Order', value: 'Double Espresso' },
      { icon: Luggage, label: 'Travel Style', value: 'Business Class, Carry-on Only' },
      { icon: Building2, label: 'Favorite Hotel Brand', value: 'Aman Resorts' },
      { icon: Plane, label: 'Favorite Airline', value: 'Delta Air Lines' },
      { icon: Compass, label: 'Dream Destination', value: 'Antarctica' }
    ]
  },
  {
    slug: 'victoria-reynolds',
    name: 'Victoria Reynolds',
    initials: 'VR',
    imgPosition: '53% 20%',
    role: 'Vice President of Global Operations',
    shortRole: 'VP, Global Operations',
    quote: 'Consistency creates trust.',
    bio: [
      'Victoria ensures every Defera booking meets the company\u2019s high operational standards. Her background spans international airline operations, travel technology, and customer experience, where she\u2019s built scalable systems serving travelers across multiple continents.',
      'Before joining Defera in 2025, she served as Global Operations Director at a European travel technology company, leading distributed teams across three time zones and managing relationships with dozens of airline carriers. Those years taught her that operational excellence is invisible when it\u2019s done right\u2014travelers simply feel taken care of.',
      'At Defera, she oversees booking operations, airline partnerships, and compliance, making sure the promise made at checkout is kept at the gate. She still keeps every boarding pass from every country she\u2019s visited\u2014a habit she says keeps her honest about what travelers actually experience.'
    ],
    passport: [
      { icon: Languages, label: 'Languages', value: 'English, French' },
      { icon: Coffee, label: 'Coffee Order', value: 'Flat White' },
      { icon: Armchair, label: 'Travel Style', value: 'Window Seat, Early Flights' },
      { icon: Building2, label: 'Favorite Hotel Brand', value: 'Four Seasons' },
      { icon: Plane, label: 'Favorite Airline', value: 'Singapore Airlines' },
      { icon: Compass, label: 'Dream Destination', value: 'Patagonia' }
    ]
  },
  {
    slug: 'luca-moretti',
    name: 'Luca Moretti',
    initials: 'LM',
    role: 'Head of Concierge',
    shortRole: 'Head of Concierge',
    quote: 'The smallest details create the strongest memories.',
    bio: [
      'Growing up in Florence, Luca learned hospitality from his grandparents\u2019 boutique hotel before studying luxury hospitality management in Switzerland. His career has included luxury hotels, executive travel, and VIP concierge services throughout Europe.',
      'Across two decades in luxury hospitality, he has arranged everything from last-minute anniversary dinners in Paris to multi-generational reunions spanning three continents. He believes great service is anticipatory\u2014solving problems travelers haven\u2019t thought of yet, and remembering the details that turn a good trip into an unforgettable one.',
      'He now leads Defera Concierge, ensuring every traveler receives thoughtful, personalized service\u2014and he still keeps a handwritten notebook of the best espresso bars in every city he visits.'
    ],
    passport: [
      { icon: Languages, label: 'Languages', value: 'Italian, English, French' },
      { icon: Coffee, label: 'Coffee Order', value: 'Espresso' },
      { icon: Building2, label: 'Favorite Hotel Brand', value: 'Rosewood' },
      { icon: Plane, label: 'Favorite Airline', value: 'Emirates' },
      { icon: Compass, label: 'Dream Destination', value: 'Kyoto during cherry blossom season' }
    ]
  },
  {
    slug: 'adrian-calloway',
    name: 'Adrian Calloway',
    initials: 'AC',
    role: 'Head of Marketing',
    shortRole: 'Head of Marketing',
    quote: 'Every great brand starts with a story worth sharing.',
    bio: [
      'Adrian combines creative storytelling with data-driven marketing to grow brands people genuinely connect with. Before Defera, he worked with venture-backed startups and creative agencies, helping transform emerging products into household names.',
      'His campaigns pair sharp creative instincts with rigorous measurement\u2014he\u2019s as comfortable in an edit suite as he is in an analytics dashboard. What drew him to Defera was the story itself: real people taking trips they thought were out of reach.',
      'At Defera, he leads brand, growth, and creative partnerships. Outside of work he travels with one backpack and a camera, and has photographed more than 200 sunrises around the world.'
    ],
    passport: [
      { icon: Languages, label: 'Languages', value: 'English' },
      { icon: Coffee, label: 'Coffee Order', value: 'Cold Brew' },
      { icon: Camera, label: 'Travel Style', value: 'One Backpack & Camera' },
      { icon: Building2, label: 'Favorite Hotel Brand', value: '1 Hotel' },
      { icon: Plane, label: 'Favorite Airline', value: 'Virgin Atlantic' },
      { icon: Compass, label: 'Dream Destination', value: 'New Zealand' }
    ]
  }
];

/* ── Leader portrait with graceful placeholder ── */
/** A contact row: a page on the site opens with the router, an address as mailto. */
function ContactRow({ to, href, ...rest }) {
  return to ? <Link to={to} {...rest} /> : <a href={href} {...rest} />;
}
ContactRow.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
};

function LeaderPortrait({ leader, className = '' }) {
  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {/* Placeholder monogram (revealed if image is missing) */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center"
        style={{ background: 'linear-gradient(160deg, #10194A 0%, #0A1233 55%, #070E28 100%)' }}
      >
        <span
          className="text-5xl sm:text-6xl font-light tracking-wide"
          style={{
            background: 'linear-gradient(135deg, #AAB4D6 0%, #7A8ABF 60%, #3D5A8C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          {leader.initials}
        </span>
      </div>
      <img
        src={`/images/team/${leader.slug}.jpg?v=${TEAM_IMG_VERSION}`}
        alt={leader.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          objectPosition: leader.imgPosition || 'center top',
          transform: leader.imgScale ? `scale(${leader.imgScale})` : undefined,
          transformOrigin: '50% 20%'
        }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    </div>
  );
}

/* ── AI leadership portrait: fills its card like LeaderPortrait, monogram if the file is missing.
      Uses the 4:5 `portrait`, not the Staff-page `avatar`, which is cropped through the head. ── */
function AgentPortrait({ person }) {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ background: 'linear-gradient(160deg, #10194A 0%, #0A1233 55%, #070E28 100%)' }}
      >
        <span
          className="text-3xl sm:text-4xl font-light tracking-wide"
          style={{
            background: 'linear-gradient(135deg, #AAB4D6 0%, #7A8ABF 60%, #3D5A8C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          {person.name[0]}
        </span>
      </div>
      <img
        src={person.portrait}
        alt={person.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center top' }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    </div>
  );
}

const COUNT_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve'];

/* ── Leader detail modal ── */
function LeaderModal({ leader, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[85vh] flex flex-col bg-[#05070F] border border-white/10 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Grab handle (mobile) */}
        <div className="sm:hidden pt-3 flex justify-center flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-4 px-5 sm:px-8 py-4 sm:py-5 border-b border-white/[0.08] flex-shrink-0">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border border-white/15 flex-shrink-0">
            <LeaderPortrait leader={leader} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg sm:text-xl font-normal text-white tracking-tight leading-tight">{leader.name}</h3>
            <p className="text-xs sm:text-sm text-brand-200/80 mt-0.5">{leader.role}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close profile"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto overscroll-contain px-5 sm:px-8 py-6 sm:py-7 space-y-7">
          {/* Quote */}
          <blockquote className="border-l-2 border-brand-400/60 pl-4 text-base sm:text-lg font-light text-white/85 italic leading-relaxed">
            &ldquo;{leader.quote}&rdquo;
          </blockquote>

          {/* Bio */}
          <div className="space-y-4">
            {leader.bio.map((p, i) => (
              <p key={i} className="text-sm text-white/80 leading-relaxed">{p}</p>
            ))}
          </div>

          {/* Passport */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-normal uppercase tracking-[0.22em] text-white/40">Passport</span>
              <PlaneTakeoff className="w-4 h-4 text-brand-300/50" />
            </div>
            <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.06] grid grid-cols-1 sm:grid-cols-2 gap-px">
              {leader.passport.map(({ icon: Icon, label, value }, i, arr) => (
                <div
                  key={label}
                  className={`bg-[#080B18] px-4 py-3.5 flex items-start gap-3 ${i === arr.length - 1 && arr.length % 2 === 1 ? 'sm:col-span-2' : ''}`}
                >
                  <Icon className="w-4 h-4 text-brand-300/70 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-white/35">{label}</p>
                    <p className="text-sm text-white/80 font-normal leading-snug">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function AboutUs() {
  const [activeLeader, setActiveLeader] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Deep link: /about?profile=<slug> opens that leader's profile
    const slug = new URLSearchParams(window.location.search).get('profile');
    if (slug) {
      const leader = LEADERS.find((l) => l.slug === slug);
      if (leader) setActiveLeader(leader);
    }
  }, []);

  const closeLeader = useCallback(() => setActiveLeader(null), []);

  const stats = [
    { icon: Users, label: 'Active Travelers', value: '50,000+' },
    { icon: MapPin, label: 'Countries Served', value: '120+' },
    { icon: TrendingUp, label: 'Customer Satisfaction', value: '4.9/5' },
    { icon: Award, label: 'Year Founded', value: '2024' }
  ];

  const values = [
    {
      icon: Users,
      title: 'Built for the underserved',
      description: "Traditional travel booking excludes millions through credit checks, upfront costs, and hidden fees. We built Defera specifically for people shut out by those systems\u2014gig workers, students, immigrants, anyone rebuilding credit, and families managing tight budgets. Our no-credit-check model isn't a feature; it's our foundation."
    },
    {
      icon: Wallet,
      title: 'Payment flexibility as a right',
      description: "The ability to spread costs over time shouldn't require perfect credit or predatory interest rates. Our installment model works like old-school layaway: you book, you pay over time, you travel when you're ready. No interest charges disguised as 'service fees.' No compound debt traps. Just transparent, manageable payments that fit how real people handle money."
    },
    {
      icon: ShieldCheck,
      title: 'Price protection that matters',
      description: "Flight prices swing wildly based on demand algorithms optimized to extract maximum revenue. That volatility punishes budget-conscious travelers who can't afford to book months ahead. Our price protection shields you from market chaos: once your booking is approved, the price we quote is the price you pay while you stay in good standing on your plan. If fares spike, you're protected."
    },
    {
      icon: Zap,
      title: 'Technology that removes friction',
      description: "We obsess over user experience because every unnecessary click, every confusing form field, and every moment of uncertainty creates barriers. Our platform uses intelligent airport search, automated payment scheduling, real-time inventory from major airlines, and AI-powered support to make booking as simple as ordering takeout. Complexity is our problem to solve, not yours."
    }
  ];

  const story = [
    {
      title: 'The Problem We Saw',
      content: "Air travel has become increasingly expensive and inaccessible. Average domestic round-trip fares in the U.S. exceed $350\u2014a prohibitive barrier for the 40% of Americans who can't cover a $400 emergency expense. Credit-based travel rewards programs, 'buy now pay later' options with hidden interest, and volatile pricing create a system that advantages the wealthy and excludes everyone else. Millions of people postpone or abandon travel plans not because they lack desire, but because they lack access to fair financing."
    },
    {
      title: 'Our Approach',
      content: "We asked a simple question: what if travel booking worked like layaway? Pay a deposit, make installment payments, receive your ticket when you've paid in full. No credit checks. No interest charges. No surprise fees. Just transparent terms and predictable costs. We built partnerships with major airlines to access real-time inventory and competitive pricing. We integrated with payment infrastructure that automates installments without friction. We designed interfaces optimized for mobile devices and slower internet connections because that's how our customers access the world."
    },
    {
      title: 'The Results',
      content: "In less than a year, we've served over 50,000 travelers across 120+ countries. Our customer satisfaction rating stands at 4.9/5. We've facilitated family reunions, enabled students to study abroad, helped workers relocate for better opportunities, and made dream vacations possible for people who'd given up on them. Our 340% quarter-over-quarter growth reflects genuine demand\u2014word spreads quickly when a service actually solves problems instead of creating new ones."
    },
    {
      title: 'What Makes Us Different',
      content: "We're not a travel agency adding markup. We're not a lender charging hidden interest. We're a platform that rethinks how travel booking should work in an era where traditional finance excludes too many people. Our business model is straightforward: transparent service fees disclosed upfront, no revenue from data sales, no predatory lending practices. We earn money when you successfully book travel, which aligns our incentives with yours. If you don't travel, we don't profit. That focus keeps us honest."
    },
    {
      title: "Where We're Going",
      content: "This is just the beginning. We're expanding partnerships with international carriers to offer more routes and better pricing. We're building AI-powered tools that suggest optimal booking times to maximize savings. We're developing group booking features for families and teams traveling together. Flight + Hotel packages already pay on the same installment model, and we're exploring car rental partnerships. Every expansion is guided by one principle: does this make travel more accessible for more people? If the answer is yes, we build it."
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  return (
    <div className="min-h-screen bg-black text-white/80">
      <PageSeo path="/about" />
      {/* ── Fixed nav ── */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10"
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <img src="/defera-logo-web.png" alt="Defera" className="h-8" />
        </div>
      </motion.div>

      {/* ── Hero ── */}
      <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 px-5 sm:px-6 border-b border-white/[0.06] overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[1100px] h-[620px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(16, 37, 178,0.32), rgba(16, 37, 178,0) 60%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, black, transparent)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, black, transparent)'
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-6xl mx-auto"
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 sm:mb-6"
            style={{ background: 'rgba(10, 18, 51,0.10)', border: '1px solid rgba(10, 18, 51,0.25)' }}
          >
            <Sparkles className="w-3 h-3" style={{ color: '#7A8ABF' }} />
            <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.18em]" style={{ color: '#AAB4D6' }}>
              Our Company · About Defera
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[72px] font-light text-white tracking-[-0.03em] leading-[1.05] mb-5 sm:mb-6">
            Travel, made possible{' '}
            <span
              className="font-normal"
              style={{
                background: 'linear-gradient(135deg, #AAB4D6 0%, #7A8ABF 50%, #2A3868 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              for everyone
            </span>
            .
          </h1>
          <p className="text-[17px] sm:text-[18px] md:text-[19px] text-white/70 leading-relaxed max-w-2xl font-light">
            We're building the travel platform that should have existed all along&mdash;one that treats access to air travel as a right, not a privilege reserved for those with perfect credit and large bank accounts.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 sm:gap-x-6 gap-y-2 mt-7 sm:mt-9 text-[11px] sm:text-xs text-white/45">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              <span>San Francisco, CA</span>
            </span>
            <span className="w-px h-3 bg-white/15" />
            <span>Founded 2024</span>
          </div>
        </motion.div>
      </section>

      {/* ── Stats band ── */}
      <section className="px-5 sm:px-6 -mt-px">
        <div className="max-w-6xl mx-auto pt-10 sm:pt-12">
          <Panel innerClassName="p-0">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {stats.map(({ icon: Icon, label, value }, i) => (
                <div
                  key={label}
                  className={`relative p-6 sm:p-7 md:p-8 transition-colors duration-300 hover:bg-white/[0.02] ${i % 2 === 0 ? 'border-r border-white/[0.06]' : ''} ${i < 2 ? 'border-b md:border-b-0 border-white/[0.06]' : ''} ${i === 1 ? 'md:border-r md:border-white/[0.06]' : ''} ${i === 2 ? 'md:border-r md:border-white/[0.06]' : ''}`}
                >
                  <Icon className="w-5 h-5 text-brand-300 mb-4" />
                  <p className="text-2xl sm:text-3xl font-light text-white mb-1">{value}</p>
                  <p className="text-xs text-white/50">{label}</p>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-5 sm:px-6">
        {/* ── Mission ── */}
        <motion.section {...fadeInUp} className="mt-16 sm:mt-20">
          <Panel className="border-brand-500/25 bg-gradient-to-br from-brand-900/40 via-brand-900/15 to-transparent" innerClassName="p-7 sm:p-8 md:p-10">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-900/60 border border-brand-500/40 flex-shrink-0">
                <Plane className="w-5 h-5 text-brand-300" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-light text-white mb-4 tracking-tight">Our Mission</h2>
                <p className="text-base text-white/80 leading-relaxed mb-4">
                  Make air travel accessible and affordable for everyone through transparent pricing, flexible payments, and technology that removes barriers instead of creating them.
                </p>
                <p className="text-sm text-white/80 leading-relaxed">
                  We believe the ability to visit family, chase opportunities, explore cultures, and create memories shouldn't depend on credit scores, bank account balances, or financial sophistication. Travel is fundamentally human. Access to it should be too.
                </p>
              </div>
            </div>
          </Panel>
        </motion.section>

        {/* ── Values ── */}
        <motion.section {...fadeInUp} className="mt-20 sm:mt-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8 sm:mb-10">
            <div>
              <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-3 block">Our Principles</span>
              <h2 className="text-2xl md:text-3xl font-light text-white tracking-tight">What We Believe</h2>
            </div>
            <p className="text-sm text-white/60 leading-relaxed md:max-w-xs md:text-right">
              Four commitments that shape every product decision we make.
            </p>
          </div>
          <Panel innerClassName="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.07]">
              {values.map((value, i) => (
                <div
                  key={value.title}
                  className="bg-[#050608] p-6 sm:p-8 hover:bg-[#080B16] transition-colors duration-300"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg border border-brand-500/30 bg-brand-900/30">
                      <value.icon className="w-4 h-4 text-brand-300" />
                    </div>
                    <span className="text-[11px] font-medium tracking-[0.25em] text-white/25 tabular-nums">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="text-base font-normal text-white mb-3 tracking-tight">{value.title}</h3>
                  <p className="text-sm text-white/80 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </Panel>
        </motion.section>

        {/* ── Leadership ── */}
        <motion.section {...fadeInUp} className="mt-20 sm:mt-24">
          <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-4 block">Meet the Team</span>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            The people behind every journey.
          </h2>
          <div className="max-w-3xl space-y-4 mb-12">
            <p className="text-base text-white/80 leading-relaxed font-normal">
              At Defera, we're travelers first.
            </p>
            <p className="text-sm text-white/80 leading-relaxed">
              Our leadership team brings together experience across technology, hospitality, operations, finance, and customer experience&mdash;all united by one belief: travel should feel exciting from the moment you begin planning.
            </p>
            <p className="text-sm text-white/80 leading-relaxed">
              Whether we're building software, helping a family reunite across the world, or finding the perfect itinerary, we approach every decision with one goal: making travel feel effortless.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {LEADERS.map((leader) => (
              <button
                key={leader.slug}
                onClick={() => setActiveLeader(leader)}
                className="group flex flex-col text-left rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] hover:border-brand-400/40 hover:bg-white/[0.05] transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60"
              >
                <div className="relative w-full aspect-[4/5] flex-shrink-0 overflow-hidden">
                  <div className="w-full h-full md:transition-transform md:duration-500 md:group-hover:scale-[1.03]">
                    <LeaderPortrait leader={leader} />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                </div>
                <div className="p-3.5 sm:p-5 flex flex-col flex-1">
                  <h3 className="text-sm sm:text-base font-normal text-white tracking-tight leading-tight">{leader.name}</h3>
                  <p className="text-[11px] sm:text-xs text-brand-200/80 mt-1 sm:mb-3">{leader.shortRole}</p>
                  <p className="hidden sm:block text-xs text-white/50 italic leading-relaxed line-clamp-2">
                    &ldquo;{leader.quote}&rdquo;
                  </p>
                  <span className="inline-flex items-center gap-1 mt-auto pt-3 sm:pt-4 text-[10px] sm:text-[11px] font-normal uppercase tracking-wider text-brand-300/70 group-hover:text-brand-200 transition-colors">
                    View Profile
                    <ArrowLeft className="w-3 h-3 rotate-180" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </motion.section>

        {/* ── SI leadership. The intro's spelled-out "super intelligence agents" is what tells visitors
              these aren't people (a bare "SI" wouldn't); keep it. ── */}
        <motion.section {...fadeInUp} className="mt-16 sm:mt-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8 sm:mb-10">
            <div>
              <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-3 block">Around the Clock</span>
              <h2 className="text-2xl md:text-3xl font-light text-white tracking-tight">Our SI Leadership</h2>
            </div>
            <p className="text-sm text-white/85 leading-relaxed md:max-w-sm md:text-right">
              {COUNT_WORDS[AI_LEADERSHIP.length] ?? AI_LEADERSHIP.length} super intelligence agents, each leading a part of the business and reporting to our leadership team.
            </p>
          </div>
          {/* More per row than the leaders (3, then 5 on desktop); flex-wrap centers the short last row. */}
          <ul className="flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-5">
            {AI_LEADERSHIP.map((person) => (
              <li
                key={person.id}
                className="flex flex-col w-[calc((100%-1.5rem)/3)] sm:w-[calc((100%-2rem)/3)] lg:w-[calc((100%-5rem)/5)] rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]"
              >
                <div className="relative w-full aspect-[4/5] flex-shrink-0 overflow-hidden">
                  <AgentPortrait person={person} />
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                </div>
                {/* Titles fit one line on desktop at 11px (longest measures 164px of 173px);
                    phone cards are too narrow for any size, so wraps are balanced instead. */}
                <div className="p-3 sm:p-4">
                  <h3 className="text-[13px] sm:text-base font-normal text-white tracking-tight leading-tight">{person.name}</h3>
                  <p className="text-[10px] sm:text-xs lg:text-[11px] text-brand-200/80 mt-1 leading-snug text-balance">{person.title}</p>
                </div>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* ── Story ── */}
        <motion.section {...fadeInUp} className="mt-20 sm:mt-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8 sm:mb-10">
            <div>
              <span className="text-xs font-normal uppercase tracking-widest text-white/40 mb-3 block">How We Got Here</span>
              <h2 className="text-2xl md:text-3xl font-light text-white tracking-tight">Our Story</h2>
            </div>
            <p className="text-sm text-white/60 leading-relaxed md:max-w-xs md:text-right">
              From a simple question to 50,000 travelers.
            </p>
          </div>
          <div className="border-t border-white/[0.08]">
            {story.map((section, i) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-10 py-7 sm:py-9 border-b border-white/[0.08]"
              >
                <div className="md:col-span-4 flex items-baseline gap-4">
                  <span className="text-[11px] font-normal tracking-[0.25em] text-brand-300/70 tabular-nums flex-shrink-0">
                    0{i + 1}
                  </span>
                  <h3 className="text-lg md:text-xl font-light text-white tracking-tight leading-snug">
                    {section.title}
                  </h3>
                </div>
                <p className="md:col-span-8 text-sm text-white/80 leading-relaxed">
                  {section.content}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* ── Why Defera ── */}
        <motion.section {...fadeInUp} className="mt-20 sm:mt-24">
          <Panel innerClassName="p-0">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-white/[0.07]">
              {/* Left: the name */}
              <div className="md:col-span-5 bg-[#050608] p-7 sm:p-9 flex flex-col justify-center relative overflow-hidden">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72"
                  style={{ background: 'radial-gradient(circle at center, rgba(16, 37, 178,0.22), transparent 65%)' }}
                />
                <span className="relative text-xs font-normal uppercase tracking-widest text-white/40 mb-5 block">
                  Why "Defera"?
                </span>
                <p
                  className="relative text-4xl sm:text-5xl font-light tracking-tight leading-none mb-3"
                  style={{
                    background: 'linear-gradient(135deg, #D5DCEF 0%, #7A8ABF 60%, #2A3868 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  de&middot;fer&middot;a
                </p>
                <p className="relative text-sm text-white/40 font-normal italic">
                  from <span className="text-white/60 not-italic">defer</span> &middot; verb
                </p>
              </div>

              {/* Right: the two meanings */}
              <div className="md:col-span-7 bg-[#050608]">
                <div className="p-6 sm:p-8 border-b border-white/[0.07]">
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="text-[11px] font-normal tracking-[0.25em] text-brand-300/70 tabular-nums flex-shrink-0">01</span>
                    <h3 className="text-base font-normal text-white tracking-tight">To put off until later</h3>
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed md:pl-9">
                    The name draws inspiration from deferring payments&mdash;making travel possible today with payment flexibility that fits your life.
                  </p>
                </div>
                <div className="p-6 sm:p-8 border-b border-white/[0.07]">
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="text-[11px] font-normal tracking-[0.25em] text-brand-300/70 tabular-nums flex-shrink-0">02</span>
                    <h3 className="text-base font-normal text-white tracking-tight">To yield with respect</h3>
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed md:pl-9">
                    We defer to our customers' needs, their financial realities, and their dreams. We don't dictate terms designed to maximize extraction&mdash;we create options designed to maximize access.
                  </p>
                </div>
                <div className="p-6 sm:p-8">
                  <p className="text-sm text-white/60 leading-relaxed italic">
                    Every element of our brand&mdash;from the minimalist design to the transparent pricing&mdash;reflects this philosophy. We're here to serve travelers, not exploit them.
                  </p>
                </div>
              </div>
            </div>
          </Panel>
        </motion.section>

        {/* ── Contact ── */}
        <motion.section {...fadeInUp} className="mt-12">
          <Panel innerClassName="p-0">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-white/[0.07]">
              {/* Left: intro + address */}
              <div className="md:col-span-5 bg-[#050608] p-7 sm:p-9 flex flex-col relative overflow-hidden">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-24 -right-24 w-72 h-72"
                  style={{ background: 'radial-gradient(circle at center, rgba(16, 37, 178,0.20), transparent 65%)' }}
                />
                <span className="relative text-xs font-normal uppercase tracking-widest text-white/40 mb-3 block">
                  Contact
                </span>
                <h3 className="relative text-2xl sm:text-3xl font-light text-white tracking-tight mb-4">
                  Get in Touch
                </h3>
                <p className="relative text-sm text-white/70 leading-relaxed">
                  Questions about our platform? Interested in partnerships? Want to share your travel story? We'd love to hear from you.
                </p>
                <div className="relative mt-auto pt-8 flex items-start gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-brand-300/70 mt-0.5 flex-shrink-0" />
                  <div className="text-white/70 leading-relaxed">
                    28 Geary St, Suite 650 #321<br />
                    San Francisco, CA 94108
                  </div>
                </div>
              </div>

              {/* Right: contact channels */}
              <div className="md:col-span-7 bg-[#050608] flex flex-col">
                {[
                  { label: 'General Inquiries', desc: 'Partnerships, ideas, and everything else', email: 'hello@defera.app' },
                  // Customers write to us with the Contact page's form; no support address is published.
                  { label: 'Customer Support', desc: 'Help with bookings and payments', to: '/contact#message', email: 'Send us a message' },
                  { label: 'Press & Media', desc: 'Interviews, assets, and story requests', email: 'press@defera.app' }
                ].map(({ label, desc, email, to }, i, arr) => (
                  <ContactRow
                    key={label}
                    to={to}
                    href={to ? undefined : `mailto:${email}`}
                    className={`group flex items-center gap-4 p-6 sm:px-8 flex-1 hover:bg-[#080B16] transition-colors duration-300 ${i < arr.length - 1 ? 'border-b border-white/[0.07]' : ''}`}
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg border border-brand-500/30 bg-brand-900/30 flex-shrink-0">
                      <Mail className="w-4 h-4 text-brand-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-normal text-white tracking-tight">{label}</p>
                      <p className="text-xs text-white/40 mt-0.5 hidden sm:block">{desc}</p>
                      <p className="text-xs text-brand-200/80 group-hover:text-brand-100 transition-colors mt-0.5 sm:hidden">
                        {email}
                      </p>
                    </div>
                    <span className="hidden sm:block text-sm text-brand-200/80 group-hover:text-brand-100 transition-colors">
                      {email}
                    </span>
                    <ArrowLeft className="w-3.5 h-3.5 rotate-180 text-white/25 group-hover:text-brand-200 group-hover:translate-x-0.5 transition-all flex-shrink-0 hidden sm:block" />
                  </ContactRow>
                ))}
              </div>
            </div>
          </Panel>
        </motion.section>

        {/* ── Award banners ── */}
        <motion.div {...fadeInUp} className="mt-12 space-y-4">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900/20 via-brand-900/10 to-brand-800/20 border-2 border-brand-500/30 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-400/5 via-brand-400/5 to-brand-400/5" />
            <div className="relative px-6 py-4 flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-xl" />
                <div className="relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-400 via-brand-400 to-brand-500 shadow-lg shadow-brand-500/50">
                  <Award className="w-6 h-6 text-brand-900" />
                </div>
                <div className="absolute top-0 right-0 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              </div>
              <div className="flex-1">
                <p className="text-sm md:text-base font-normal text-brand-100 leading-relaxed">
                  <span className="font-medium text-brand-200">Defera</span> is the world's first travel layaway service that offers a <span className="font-medium text-brand-200">reward system</span>
                </p>
                <p className="text-xs text-brand-300/60 mt-1 hidden md:block">
                  Triple dip by earning credit card points, frequent flyer miles, and Defera Miles
                </p>
              </div>
              <div className="hidden md:flex gap-1">
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900/20 via-brand-900/10 to-brand-800/20 border-2 border-brand-500/30 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-400/5 via-brand-400/5 to-brand-400/5" />
            <div className="relative px-6 py-4 flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-xl" />
                <div className="relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-400 via-brand-400 to-brand-500 shadow-lg shadow-brand-500/50">
                  <PlaneTakeoff className="w-6 h-6 text-brand-900" />
                </div>
                <div className="absolute top-0 right-0 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              </div>
              <div className="flex-1">
                <p className="text-sm md:text-base font-normal text-brand-100 leading-relaxed">
                  <span className="font-medium text-brand-200">Defera</span> is the world's first travel layaway service that offers <span className="font-medium text-brand-200">semi-private flights</span>
                </p>
                <p className="text-xs text-brand-300/60 mt-1 hidden md:block">
                  Book JSX semi-private flights with flexible payment plans
                </p>
              </div>
              <div className="hidden md:flex gap-1">
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Footer note ── */}
        <div className="mt-16 pt-6 border-t border-white/5 pb-4">
          <p className="text-xs text-white/40 text-center leading-relaxed">
            Defera LLC • San Francisco, CA • Making travel accessible since 2024
          </p>
        </div>
      </div>

      <Footer />

      {/* ── Leader modal ── */}
      <AnimatePresence>
        {activeLeader && <LeaderModal leader={activeLeader} onClose={closeLeader} />}
      </AnimatePresence>
    </div>
  );
}
